<template>
  <div>
    <div class="tw-w-full tw-overflow-x-auto">
    <table class="tw-min-w-full tw-text-left tw-text-sm tw-table-fixed">
      <thead>
        <tr class="tw-border-b tw-border-slate-200">
          <th class="tw-whitespace-nowrap tw-px-2 tw-py-2 tw-font-semibold tw-text-slate-700 tw-w-7">#</th>
          <th class="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold tw-text-slate-700 tw-w-32">Type</th>
          <th class="tw-whitespace-nowrap tw-px-2 tw-py-2 tw-font-semibold tw-text-slate-700 tw-w-32 ">Scheduled</th>
          <th class="tw-px-3 tw-py-2 tw-font-semibold tw-text-slate-700 tw-w-56">Origin</th>
          <th class="tw-px-3 tw-py-2 tw-font-semibold tw-text-slate-700 tw-w-56">Destination</th>
          <th v-if="showDriver" class="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold tw-text-slate-700 tw-w-56">Driver</th>
          <th class="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold tw-text-slate-700 tw-w-36">Chassis</th>
          <th class="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold tw-text-slate-700 tw-w-56"></th>
        </tr>
      </thead>
      <tbody class="tw-align-top">
      <template v-for="(trip, tripIdx) in sortedTrips" :key="trip.id">
        <ContainerAddTripRow v-if="
          editable &&
          !container.isCancelled &&
          tripAdditionContainer === container.id &&
          editingTrip.beforeLegId == tripIdx
        " :editingTrip="editingTrip" @confirmAddTrip="confirmAddTrip(container, order.id)"
          @cancelTripAddition="cancelTripAddition" @addChassisTag="addChassisTag" :terminals="terminals"
          :filterDrivers="filterDrivers" :filteredChassis="filteredChassis" :destinationCustomers="destinationCustomers"
          :originCustomers="originCustomers" @onOriginCustomerSearch="onOriginCustomerSearch"
          @onDestinationCustomerSearch="onDestinationCustomerSearch"></ContainerAddTripRow>
        <tr class="tw-border-b tw-border-slate-100 hover:tw-bg-slate-50/50"
          :class="editingTrip.id === trip.id ? 'tw-bg-indigo-50/40' : ''">
          
          <!-- Trip Leg ID/Index -->
          <td class="tw-whitespace-nowrap tw-w-7 tw-px-2 tw-py-2 tw-text-slate-700">{{ tripIdx }}</td>

          <!-- Trip Type-->
          <td class="tw-w-32">
            <TripTypeField autofocus :showLabel="false" :trip="editingTrip.id === trip.id ? editingTrip : trip"
              :isEditing="editingTrip.id === trip.id" :darkGreen="trip.status == 'Complete'"
              :gray="trip.status != 'Complete' && trip.status != 'In Progress'" :green="trip.status == 'In Progress'">
            </TripTypeField>
          </td>

          <!-- Scheduled Date -->
          <td v-if="editingTrip.id === trip.id" class="tw-px-1 tw-py-2 tw-w-32 dispatch-trip-edit-scheduled-date">
            <DatePicker class="tw-block"
              :input-class="'tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55'"
              format="MM/DD/YYYY" v-model="editingTrip.scheduledDate"
              :show-days-shortcuts="true"
              controls-placement="popup"
            ></DatePicker>
          </td>
          <td v-else class="tw-whitespace-nowrap tw-px-2 tw-py-2 tw-text-slate-700 tw-w-32 ">
            {{ trip.scheduledDate ? formatDate(trip.scheduledDate) : '--' }}
          </td>

          <!-- Origin -->
          <td v-if="editingTrip.id === trip.id" class="tw-px-1 tw-py-2 tw-w-56">
            <FilterableDropdown :showLabels="false" placeholder="Origin" size="sm" v-model="editingTrip.origin"
              :customLabel="(x) => (x.shortName ? x.shortName : x.name)" :options="combinedOriginOptions"
              :class="caller === 'dispatch' ? 'tw-w-[180px]' : 'tw-w-[180px]'"
              @search-change="onOriginCustomerSearch" />
          </td>
          <td v-else-if="trip.origin" :title="fullAddress(trip.origin.address)"
            class="tw-px-3 tw-py-2 tw-text-slate-700 tw-w-56">
            {{ nameForLocation(trip.origin) }}
          </td>
          <td v-else class="tw-px-3 tw-py-2 tw-text-slate-700 tw-w-56">
            {{ nameForLocation(trip.origin) }}
          </td>

          <!-- Destination -->
          <td v-if="editingTrip.id === trip.id" class="tw-px-1 tw-py-2 tw-w-56">
            <FilterableDropdown :showLabels="false" placeholder="Destination" size="sm"
              v-model="editingTrip.destination" :customLabel="(x) => (x.shortName ? x.shortName : x.name)"
              :options="combinedDestinationOptions" :class="caller === 'dispatch' ? 'tw-w-[180px]' : 'tw-w-[180px]'"
              @search-change="onDestinationCustomerSearch" />
          </td>
          <td v-else-if="trip.destination" :title="fullAddress(trip.destination.address)"
            class="tw-px-3 tw-py-2 tw-text-slate-700 tw-w-56">
            {{ nameForLocation(trip.destination) }}
          </td>
          <td v-else class="tw-px-3 tw-py-2 tw-text-slate-700 tw-w-56">
            {{ nameForLocation(trip.destination) }}
          </td>

          <!-- Driver -->
          <td v-if="editingTrip.id === trip.id" class="tw-w-56 tw-px-1 tw-py-2">
            <FilterableDropdown :showLabels="false" placeholder="Driver" size="sm" v-model="editingTrip.driver"
              label="fullName" :options="filterDrivers" />
          </td>
          <td v-else-if="showDriver" class="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-slate-700 tw-w-56">
            {{ trip.driver ? trip.driver.fullName : '' }}
          </td>

          <!-- Chassis -->
          <td v-if="editingTrip.id === trip.id" class="tw-px-1 tw-py-2 tw-w-36">
            <input type="text"
              class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55 tw-w-[100%]"
              placeholder="Chassis Number"
              v-model="editingTrip.chassis" />
          </td>
          <td v-else class="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-slate-700">
            <CopyButton
              :value="trip.chassis"
              mode="wrap-content"
              title="Copy chassis number"
              aria-label="Copy chassis number to clipboard"
            >
              {{ trip.chassis }}
            </CopyButton>
          </td>

          <!-- Actions -->
          <td class="tw-whitespace-nowrap tw-px-0 tw-py-2">
            <div class="tw-flex tw-justify-end tw-items-center tw-gap-1">
              <template v-if="editable && editingTrip.id === trip.id">
                <Button 
                  :isGreen="true" class="tw-h-9 tw-w-9 tw-p-0"
                  @click.stop="saveTrip(editingTrip)"
                  :enableLoading="loading" 
                  :disabled="loading" 
                  title="Save container details" 
                  aria-label="Save container details"
                  icon="check"
                />
                <Button 
                  :isRed="true" :isSecondary="true" class="tw-h-9 tw-w-9 tw-p-0"
                  @click="cancelEditTrip" 
                  :disabled="loading"
                  title="Cancel edit container details" 
                  aria-label="Cancel edit container details"
                  icon="xmark"
                />
              </template>

              <template v-if="editable && editingTrip.id !== trip.id">
                <button v-if="isStartCandidate(trip)" type="button"
                  class="tw-inline-flex tw-items-center tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white"
                  :class="canStartTrip(tripIdx)
                    ? 'tw-bg-emerald-600 hover:tw-bg-emerald-700'
                    : 'tw-bg-slate-300 tw-cursor-not-allowed'" :disabled="!canStartTrip(tripIdx) || isTripActionPending(trip.id)"
                  @click.stop="startTrip(trip, tripIdx)">
                  <div
                    v-if="isTripActionPending(trip.id) && actionLoadingType === 'start'"
                    class="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-white/40 tw-border-t-white"
                  ></div>
                  {{ isTripActionPending(trip.id) && actionLoadingType === 'start' ? 'Starting...' : 'Start Trip' }}
                </button>
                <button v-else-if="canCompleteTrip(tripIdx)" type="button"
                  class="tw-inline-flex tw-items-center tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700"
                  :disabled="isTripActionPending(trip.id)"
                  @click.stop="completeTrip(trip, tripIdx)">
                  <div
                    v-if="isTripActionPending(trip.id) && actionLoadingType === 'complete'"
                    class="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-white/40 tw-border-t-white"
                  ></div>
                  {{ isTripActionPending(trip.id) && actionLoadingType === 'complete' ? 'Completing...' : 'Complete Trip' }}
                </button>
              </template>

              <button
                v-if="editingTrip.id !== trip.id"
                type="button"
                :class="Array.isArray(trip.tripNotes) && trip.tripNotes.length > 0
                  ? 'tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-emerald-300 tw-bg-emerald-50 tw-p-1 tw-m-1 tw-text-xs tw-font-semibold tw-text-emerald-700 hover:tw-bg-emerald-100 tw-relative'
                  : 'tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-300 tw-bg-slate-100 tw-p-1 tw-m-1 tw-text-xs tw-font-semibold tw-text-slate-900 hover:tw-bg-slate-400 tw-relative'"
                aria-label="View trip notes"
                v-tippy="`View ${trip?.tripNotes?.length} trip note${trip?.tripNotes?.length > 1 ? 's':''}`"
                @click.stop="openTripNotes(trip)"
              >
                <font-awesome-icon
                  :icon="['fas', 'sticky-note']"
                  :class="Array.isArray(trip.tripNotes) && trip.tripNotes.length > 0 ? 'tw-text-emerald-700' : 'tw-text-black'"
                />
                <span
                  v-if="Array.isArray(trip.tripNotes) && trip.tripNotes.length > 0"
                  class="tw-ml-1 
                  tw-inline-flex 

                  tw-items-center tw-justify-center 
                  tw-rounded-full tw-border 
                  tw-h-5 tw-min-w-[10px] tw-px-1.5 
                  tw-text-[10px] tw-font-bold 
                  tw-bg-emerald-600 tw-text-white tw-border-emerald-600
                  tw-absolute"
                  style="top: -12px; right: -12px;"
                >
                  {{ trip.tripNotes.length }}
                </span>
              </button>

              <TripRowActionsMenu v-if="editable && editingTrip.id !== trip.id" :trip="trip" :containerId="container.id"
                :orderId="order.id" :isEditing="editingTrip.id === trip.id" :TRIP_STATUS="TRIP_STATUS"
                :canStartTrip="caller === 'dispatch' ? isNotStartedStatus(trip?.status) : canStartTrip(tripIdx)"
                :canCompleteTrip="caller === 'dispatch' ? isInProgressStatus(trip?.status) : canCompleteTrip(tripIdx)"
                :isActionPending="isTripActionPending(trip.id)"
                @edit="editTrip(trip)" @addBefore="addTripBefore(trip, container.id)"
                @addAfter="addTripAfter(trip, container.id)" @startTrip="startTrip(trip, tripIdx)"
                @completeTrip="completeTrip(trip, tripIdx)" @revertTrip="revertTrip(trip, container.id)"
                @cancelTrip="cancelTrip(trip)" @save="saveTrip(editingTrip)" @cancelEdit="cancelEditTrip" />
            </div>
          </td>
        </tr>

        <ContainerAddTripRow v-if="
          editable &&
          !container.isCancelled &&
          tripAdditionContainer === container.id &&
          editingTrip.afterLegId == tripIdx
        " :editingTrip="editingTrip" @confirmAddTrip="confirmAddTrip(container, order.id)"
          @cancelTripAddition="cancelTripAddition" :terminals="terminals" :filterDrivers="filterDrivers"
          :filteredChassis="filteredChassis" :destinationCustomers="destinationCustomers"
          :originCustomers="originCustomers" @onOriginCustomerSearch="onOriginCustomerSearch"
          @onDestinationCustomerSearch="onDestinationCustomerSearch"></ContainerAddTripRow>
      </template>
      </tbody>
    </table>
    </div>
    <AddNotesModal
      :open="tripNotesModalOpen"
      title="Trip Notes"
      :subtitle="selectedTripForNotes ? `Trip #${selectedTripForNotes.id}` : undefined"
      :trip="selectedTripForNotes"
      @close="closeTripNotes"
      @addedNote="updateTripNotes"
      @archivedNotesUpdated="updateTripArchivedNotes"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DatePicker from '@/components/core/DateTime/DatePicker.vue';
import { pushToast } from '@/lib/toast';
import moment from 'moment';

import EditTripStatus from '@/gql/orders/trips/mutations/EditTripStatus.gql';
import AddTripMutation from '../../gql/dispatch/AddTrip.gql';
import EditTripMutation from '../../gql/dispatch/EditTrip.gql';
import CancelTripMutation from '../../gql/orders/trips/mutations/CancelTrip.gql';
import ReceivingCustomersQuery from '../../gql/customers/ReceivingCustomers.gql';
import OrdersQuery from '../../gql/orders/Orders.gql';
import DispatchContainers from '../../gql/dispatch/DispatchContainers.gql';
import TerminalsQuery from '../../gql/terminals/Terminals.gql';
import ContainerAddTripRow from './OrderDetail/ContainerAddTripRow.vue';

import TripTypeField from './UI/TripTypeField.vue';
import FilterableDropdown from '../core/FilterableDropdown.vue';
import Button from '../core/Button.vue';

import { yardLocations } from '../../utils/locations';
import { TRIP_STATUS } from '../../types';
import { dateFormatMixin } from '../../utils/date';
import { addressMixin } from '../../utils/address';

import TripRowActionsMenu from './UI/TripRowActionsMenu.vue';
import AddNotesModal from '@/components/core/AddNotesModal.vue';
import CopyButton from '../core/CopyButton.vue';

export default defineComponent({
  components: {
    TripTypeField,
    FilterableDropdown,
    Button,
    ContainerAddTripRow,
    DatePicker,
    TripRowActionsMenu,
    AddNotesModal,
    CopyButton,
  },
  mixins: [dateFormatMixin, addressMixin],
  props: {
    caller: {
      type: String,
    },
    trips: {
      type: Array,
      default: () => [],
    },
    container: {
      type: Object,
    },
    order: {
      type: Object,
    },
    drivers: {
      type: Array,
      default: () => [],
    },
    chassis: {
      type: Array,
      default: () => [],
    },
    editable: {
      default: true,
    },
    showDriver: {
      default: true,
    },
  },
  data(): any {
    return {
      loading: false,
      yardLocations,
      tripAdditionContainer: undefined,
      originCustomerNameQuery: '',
      destinationCustomerNameQuery: '',
      originCustomers: [],
      destinationCustomers: [],
      terminals: [],
      newChassis: [],
      actionLoadingTripId: undefined,
      actionLoadingType: '',
      tripNotesModalOpen: false,
      selectedTripForNotes: undefined,
      // trips are a prop so we cannot overwrite it upon note udpates, so let's use an override as a hack/fix
      tripNotesOverrides: {} as Record<string, any[]>,

      editingTrip: {
        id: undefined,
        afterLegId: undefined,
        beforeLegId: undefined,
        type: undefined,
        origin: undefined,
        destination: undefined,
        driver: undefined,
        chassis: undefined,
        scheduledDate: undefined,
        isEditingDriver: false,
        isEditingChassis: false,
        isEditingDestination: false,
      },
    };
  },
  created() {
    this.TRIP_STATUS = TRIP_STATUS;
  },
  computed: {
    combinedOriginOptions(): any[] {
      const vm = (this as any);
      const options = ((vm.originCustomers || []) as any[]).concat((vm.terminals || []) as any[]);
      if (vm.editingTrip.origin && !options.find(x => String(x.id) === String(vm.editingTrip.origin.id))) {
        options.push(vm.editingTrip.origin);
      }
      return options;
    },
    combinedDestinationOptions(): any[] {
      const vm = (this as any);
      const options = ((vm.destinationCustomers || []) as any[]).concat((vm.terminals || []) as any[]);

      if (vm.editingTrip.destination && !options.find(x => String(x.id) === String(vm.editingTrip.destination.id))) {
        options.push(vm.editingTrip.destination);
      }
      return options;
    },
    hasEditingTrip(): boolean {
      const vm = this as any;
      const id = vm.editingTrip?.id;
      return id != null && id !== -1;
    },
    sortedTrips(): any {
      const vm = (this as any);
      const overrides = vm.tripNotesOverrides;
      const sortedTrips = vm.trips.map(t => {
        const copy = { ...t };
        if (overrides[String(t.id)]) {
          copy.tripNotes = overrides[String(t.id)];
        }
        return copy;
      }).sort((a, b) => {
        return Number(a.legId) < Number(b.legId) ? -1 : 1;
      });

      return sortedTrips;
    },
    filterDrivers(): any[] {
      return (this as any).drivers;
    },
    filteredChassis(): any[] {
      const vm = (this as any);

      if (!vm.chassis) {
        return [].concat(vm.newChassis);
      } else {
        return vm.chassis.concat(vm.newChassis);
      }
    },
    getIsContainerCancelled(): boolean {
      return Boolean((this as any).container?.isCancelled);
    },
  },
  methods: {
    getGraphqlErrorMessage(err: any, fallback: string): string {
      return err?.graphQLErrors?.[0]?.message || err?.message || fallback;
    },
    getIsTripEditAllowed(): boolean {
      if ((this as any).getIsContainerCancelled) {
        pushToast(
          'This container is cancelled, trips cannot be created or updated.',
          'warning',
          5000,
        );
        return false;
      }
      return true;
    },
    normalizedStatus(status: any): string {
      if (status == null) return '';
      return String(status).trim().toLowerCase();
    },
    isCompleteStatus(status: any): boolean {
      return (this as any).normalizedStatus(status) === (this as any).normalizedStatus(TRIP_STATUS.COMPLETE);
    },
    isInProgressStatus(status: any): boolean {
      return (this as any).normalizedStatus(status) === (this as any).normalizedStatus(TRIP_STATUS.IN_PROGRESS);
    },
    isNotStartedStatus(status: any): boolean {
      const s = (this as any).normalizedStatus(status);
      return !s || s === (this as any).normalizedStatus(TRIP_STATUS.NOT_STARTED);
    },
    isStartCandidate(trip: any): boolean {
      const vm = (this as any);
      return vm.isNotStartedStatus(trip?.status);
    },
    isTripActionPending(tripId: string | number): boolean {
      const vm = this as any;
      return String(vm.actionLoadingTripId) === String(tripId);
    },
    previousChainComplete(tripIdx: number): boolean {
      const vm = (this as any);
      const trips = vm.sortedTrips || [];
      for (let i = 0; i < tripIdx; i++) {
        if (!vm.isCompleteStatus(trips[i]?.status)) return false;
      }
      return true;
    },
    firstInProgressIdx(): number {
      const vm = (this as any);
      const trips = vm.sortedTrips || [];
      return trips.findIndex((t: any) => vm.isInProgressStatus(t?.status));
    },
    canStartTrip(tripIdx: number): boolean {
      const vm = (this as any);
      if (vm.caller === 'dispatch') {
        const trips = vm.sortedTrips || [];
        const trip = trips[tripIdx];
        return vm.isNotStartedStatus(trip?.status);
      }
      const trips = vm.sortedTrips || [];
      const trip = trips[tripIdx];
      return vm.isNotStartedStatus(trip?.status);
    },
    canCompleteTrip(tripIdx: number): boolean {
      const vm = (this as any);
      if (vm.caller === 'dispatch') {
        const trips = vm.sortedTrips || [];
        const trip = trips[tripIdx];
        return vm.isInProgressStatus(trip?.status);
      }
      const trips = vm.sortedTrips || [];
      const trip = trips[tripIdx];
      return vm.isInProgressStatus(trip?.status);
    },
    nameForLocation(location): string {
      if (!location) return '';
      if (location.shortName) return location.shortName;
      else return location.name;
    },
    cancelEditTrip() {
      (this as any).editingTrip.id = -1;
    },
    editTrip(trip) {
      const vm = (this as any);
      if (!vm.getIsTripEditAllowed()) {
        return;
      }
      vm.originCustomerNameQuery = '';
      vm.destinationCustomerNameQuery = '';

      const originOptions = ((vm.originCustomers || []) as any[]).concat((vm.terminals || []) as any[]);
      const destOptions = ((vm.destinationCustomers || []) as any[]).concat((vm.terminals || []) as any[]);
      const driverOptions = (vm.filterDrivers || []) as any[];

      const originId = trip?.origin?.id;
      const destId = trip?.destination?.id;
      const driverId = trip?.driver?.id;

      const mappedOrigin = originId != null ? originOptions.find((x: any) => String(x?.id) === String(originId)) : undefined;
      const mappedDestination = destId != null ? destOptions.find((x: any) => String(x?.id) === String(destId)) : undefined;
      const mappedDriver = driverId != null ? driverOptions.find((x: any) => String(x?.id) === String(driverId)) : undefined;

      let scheduledDate: any = trip?.scheduledDate;
      if (scheduledDate != null && !(scheduledDate instanceof Date)) {
        const m = moment(scheduledDate);
        scheduledDate = m.isValid() ? m.toDate() : undefined;
      }

      vm.editingTrip = {
        ...trip,
        // Normalize field names used across add/edit flows
        type: trip.type || trip.tripType,
        tripType: trip.tripType || trip.type,
        scheduledDate: scheduledDate,
        chassis: trip.chassis || '',
        origin: mappedOrigin || (trip.origin ? { ...trip.origin } : undefined),
        destination: mappedDestination || (trip.destination ? { ...trip.destination } : undefined),
        driver: mappedDriver || (trip.driver ? { ...trip.driver } : undefined),
      };
    },
    addTripBefore(trip: any, containerId: string | number) {
      const vm = (this as any);
      if (!vm.getIsTripEditAllowed()) {
        return;
      }

      vm.tripAdditionContainer = containerId;
      vm.editingTrip.beforeLegId = trip.legId;
      vm.editingTrip.origin = trip.destination;
      vm.editingTrip.destination = vm.order.receiver;
      vm.editingTrip.driver = trip.driver;
      vm.editingTrip.chassis = trip.chassis;
    },
    addTripAfter(trip: any, containerId: any) {
      const vm = (this as any);
      if (!vm.getIsTripEditAllowed()) {
        return;
      }

      vm.tripAdditionContainer = containerId;
      vm.editingTrip.afterLegId = trip.legId;
      vm.editingTrip.origin = trip.destination;
      vm.editingTrip.destination = vm.order.receiver;
      vm.editingTrip.driver = trip.driver;
      vm.editingTrip.chassis = trip.chassis;
    },
    cancelTripAddition() {
      const vm = (this as any);

      vm.tripAdditionContainer = undefined;
      vm.editingTrip.afterLegId = undefined;
      vm.editingTrip.beforeLegId = undefined;
    },
    confirmAddTrip(container: any, orderId: any) {
      const vm = (this as any);

      if (!vm.getIsTripEditAllowed()) {
        return;
      }

      vm.addTripLoading = true;
      if (!vm.editingTrip.type) {
        return;
      }
      if (!vm.editingTrip.origin) {
        return;
      }
      if (!vm.editingTrip.destination) {
        return;
      }
      vm.$apollo
        .mutate({
          mutation: AddTripMutation,
          variables: {
            containerId: Number(container.id),
            orderId: Number(orderId),
            afterLegId: vm.editingTrip.afterLegId,
            beforeLegId: vm.editingTrip.beforeLegId,
            tripType: vm.editingTrip.type,
            originId: vm.editingTrip.origin.id,
            destinationId: vm.editingTrip.destination.id,
            driverId: vm.editingTrip.driver?.id,
            chassis: vm.editingTrip.chassis,
            scheduledDate: vm.editingTrip.scheduledDate,
          },
          update: (store, { data: addTrip }) => {
            const vm = (this as any);

            if (vm.caller === 'orders') {
              const data: any = store.readQuery({
                query: OrdersQuery,
              });
            } else if (vm.caller === 'dispatch') {
              const tripsForOrder = addTrip.addTrip.order.trips;
              const storeData: any = store.readQuery({
                query: DispatchContainers,
              });
              const tripsForContainer = tripsForOrder.filter(
                (t) => t.container.id === container.id,
              );

              const updatedStoreData = { ...storeData };
              if (updatedStoreData.dispatchContainers) {
                updatedStoreData.dispatchContainers.map((dContainer) => {
                  if (dContainer.id === container.id) {
                    dContainer.trips = tripsForContainer;
                  }
                  return dContainer;
                });

                store.writeQuery({
                  query: DispatchContainers,
                  data: {
                    dispatchContainers: updatedStoreData.dispatchContainers,
                  },
                });
              }
            }
          },
        })
        .then((data) => {
          const vm = (this as any);

          vm.tripAdditionContainer = undefined;
          vm.editingTrip = {
            id: undefined,
            afterLegId: undefined,
            type: undefined,
            origin: undefined,
            destination: undefined,
            driver: undefined,
            chassis: undefined,
            isEditingDriver: false,
            isEditingChassis: false,
            isEditingDestination: false,
          };
          vm.addTripLoading = false;
          vm.$emit('refreshOrders');
        })
        .catch((err: any) => {
          console.error(err);
          vm.tripAdditionContainer = undefined;
          vm.addTripLoading = false;
          pushToast(
            vm.getGraphqlErrorMessage(err, 'Unable to add trip. Please try again.'),
            'error',
            5000,
          );
        });
    },
    startTrip(trip, tripIdx?: number) {
      const vm = (this as any);
      if (!vm.getIsTripEditAllowed()) {
        return;
      }
      if (vm.isTripActionPending(trip.id)) return;

      if (vm.caller !== 'dispatch') {
        if (typeof tripIdx === 'number' && !vm.canStartTrip(tripIdx)) {
          pushToast('Complete the previous trip before starting this one.', 'error');
          return;
        }
      }

      vm.loading = true;
      vm.actionLoadingTripId = trip.id;
      vm.actionLoadingType = 'start';
      vm.$apollo
        .mutate({
          mutation: EditTripStatus,
          variables: {
            tripId: Number(trip.id),
            status: TRIP_STATUS.IN_PROGRESS,
          },
        })
        .then(() => {
          vm.loading = false;
          vm.$emit('refreshOrders');
          vm.editingTrip.id = undefined;
        })
        .catch((err: any) => {
          vm.loading = false;
          pushToast(
            vm.getGraphqlErrorMessage(err, 'Unable to start the trip. Please try again.'),
            'error',
            5000,
          );
        })
        .finally(() => {
          vm.actionLoadingTripId = undefined;
          vm.actionLoadingType = '';
        });
    },
    cancelTrip(trip) {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: CancelTripMutation,
          variables: {
            tripId: trip.id,
          },
        })
        .then((data) => {
          console.log('trip cancelled');
          vm.$emit('refreshOrders');
        })
        .catch((err: any) => {
          console.error(err);
          pushToast(
            vm.getGraphqlErrorMessage(err, 'Unable to cancel trip. Please try again.'),
            'error',
            5000,
          );
        });
    },
    revertTrip(trip: any) {
      const vm = (this as any);

      if (!vm.getIsTripEditAllowed()) {
        return;
      }

      vm.loading = true;
      const currentStatus = trip.status;
      const nextStatus =
        currentStatus === TRIP_STATUS.COMPLETE
          ? TRIP_STATUS.IN_PROGRESS
          : TRIP_STATUS.NOT_STARTED;
      vm.$apollo
        .mutate({
          mutation: EditTripStatus,
          variables: {
            tripId: Number(trip.id),
            status: nextStatus,
          },
        })
        .then(() => {
          vm.loading = false;
          vm.editingTrip.id = undefined;
          vm.$emit('refreshOrders');
        })
        .catch((err: any) => {
          vm.loading = false;
          pushToast(
            vm.getGraphqlErrorMessage(err, 'Unable to revert trip. Please try again.'),
            'error',
            5000,
          );
        });
    },
    completeTrip(trip, tripIdx?: number) {
      const vm = (this as any);
      if (!vm.getIsTripEditAllowed()) {
        return;
      }
      if (vm.isTripActionPending(trip.id)) return;

      if (vm.caller !== 'dispatch') {
        if (typeof tripIdx === 'number' && !vm.canCompleteTrip(tripIdx)) {
          pushToast('Complete the previous trip before completing this one.', 'error');
          return;
        }
      }

      vm.loading = true;
      vm.actionLoadingTripId = trip.id;
      vm.actionLoadingType = 'complete';
      vm.$apollo
        .mutate({
          mutation: EditTripStatus,
          variables: {
            tripId: Number(trip.id),
            status: 'Complete',
          },
        })
        .then(() => {
          vm.loading = false;
          vm.$emit('refreshOrders');
          vm.editingTrip.id = undefined;
        })
        .catch((err: any) => {
          vm.loading = false;
          pushToast(
            vm.getGraphqlErrorMessage(err, 'Unable to complete the trip. Please try again.'),
            'error',
            5000,
          );
        })
        .finally(() => {
          vm.actionLoadingTripId = undefined;
          vm.actionLoadingType = '';
        });
    },
    addChassisTag(chassis: string, trip: any) {
      const vm = (this as any);

      vm.newChassis.push(chassis);
      trip.chassis = chassis;
    },
    saveTrip(trip) {
      const vm = (this as any);
      if (!vm.getIsTripEditAllowed()) {
        return;
      }
      vm.loading = true;

      const variables = {
        tripId: String(trip.id),
        tripType: trip.tripType || trip.type,
        originId: trip.origin?.id ? String(trip.origin.id) : null,
        destinationId: trip.destination?.id ? String(trip.destination.id) : null,
        driverId: trip.driver?.id ? String(trip.driver.id) : null,
        chassis: trip.chassis || '',
        scheduledDate: trip.scheduledDate,
      };

      vm.$apollo
        .mutate({
          mutation: EditTripMutation,
          variables: variables,
        })
        .then((result) => {
          vm.editingTrip.id = undefined;
          vm.loading = false;
          vm.$emit('refreshOrders');
        })
        .catch((err: any) => {
          console.error('GraphQL Error:', err);
          vm.loading = false;
          pushToast(
            vm.getGraphqlErrorMessage(
              err,
              'Unable to save trip. Please try again.',
            ),
            'error',
            5000,
          );
        });
    },
    onOriginCustomerSearch(customerName) {
      (this as any).originCustomerNameQuery = customerName;
    },
    onDestinationCustomerSearch(customerName) {
      (this as any).destinationCustomerNameQuery = customerName;
    },
    printDispatchURL(containerId, tripId, orderId) {
      return `/print/order/dispatch/${orderId}/container/${containerId}/trip/${tripId}`;
    },

    printBLUrl(containerIdx: any, orderId: any) {
      return `/print/order/bl/${orderId}/container/${containerIdx}`;
    },
    openTripNotes(trip: any) {
      const vm = this as any;
      vm.selectedTripForNotes = trip;
      vm.tripNotesModalOpen = true;
    },
    closeTripNotes() {
      const vm = this as any;
      vm.tripNotesModalOpen = false;
      vm.selectedTripForNotes = undefined;
    },
    updateTripNotes(notes: any[]) {
      const vm = this as any;
      const tripId = vm.selectedTripForNotes?.id;
      if (!tripId) return;
      vm.tripNotesOverrides = { ...vm.tripNotesOverrides, [String(tripId)]: notes };
      if (vm.selectedTripForNotes) {
        vm.selectedTripForNotes = { ...vm.selectedTripForNotes, tripNotes: notes };
      }
    },
    updateTripArchivedNotes(archivedNotes: any[]) {
      const vm = this as any;
      if (vm.selectedTripForNotes) {
        vm.selectedTripForNotes = { ...vm.selectedTripForNotes, archivedTripNotes: archivedNotes };
      }
    },
  },
  ...{
    apollo: {
      originCustomers: {
        query: ReceivingCustomersQuery,
        debounce: 100,
        variables() {
          return {
            name: (this as any).originCustomerNameQuery,
          };
        },
        update: (data) => data.receivingCustomers,
      },
      destinationCustomers: {
        query: ReceivingCustomersQuery,
        debounce: 100,
        variables() {
          return {
            name: (this as any).destinationCustomerNameQuery,
          };
        },
        update: (data) => data.receivingCustomers,
      },
      terminals: {
        query: TerminalsQuery,
      },
    },
  },
});
</script>

<style scoped>
table td {
  vertical-align: middle;
}
:deep(.dispatch-trip-editor__scheduled .mx-datepicker) {
  width: 100%;
}

:deep(.dispatch-trip-editor__scheduled .mx-datepicker .mx-input-wrapper) {
  width: 100%;
  overflow: hidden;
}

:deep(.dispatch-trip-editor__scheduled .mx-datepicker .mx-input) {
  width: 100%;
  box-sizing: border-box;
  padding-right: 2rem;
}

:deep(.dispatch-trip-editor__scheduled .mx-datepicker .mx-icon-calendar),
:deep(.dispatch-trip-editor__scheduled .mx-datepicker .mx-icon-clear) {
  right: 0.5rem;
}

.dispatch-trip-edit-scheduled-date :deep(.mx-datepicker) {
  width: 100% !important;
}
.dispatch-trip-edit-scheduled-date :deep(.mx-datepicker) input {
  padding: .5rem !important;
}
</style>